import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FiArrowRight, FiUserPlus, FiX } from 'react-icons/fi';
import { useAuth } from '../auth/AuthContext';

const HIDDEN_PATHS = ['/login', '/register', '/verify', '/auth', '/forgot-password', '/reset-password', '/admin', '/client', '/publicar', '/editar'];
const STORAGE_KEY = 'nido:guest-welcome-dismissed';

export default function GuestWelcomePrompt() {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(true);
  const [cookieChosen, setCookieChosen] = useState(false);
  const [installVisible, setInstallVisible] = useState(false);

  useEffect(() => {
    if (loading || user) return;
    try { setDismissed(sessionStorage.getItem(STORAGE_KEY) === 'true'); }
    catch { setDismissed(false); }
  }, [loading, user]);

  useEffect(() => {
    const updateCookie = () => setCookieChosen(true);
    const showInstall = () => setInstallVisible(true);
    const hideInstall = () => setInstallVisible(false);
    try { setCookieChosen(Boolean(localStorage.getItem('nido_privacy_choice_v1'))); } catch { setCookieChosen(true); }
    window.addEventListener('nido:cookie-choice', updateCookie);
    window.addEventListener('nido:install-visible', showInstall);
    window.addEventListener('nido:install-dismissed', hideInstall);
    return () => {
      window.removeEventListener('nido:cookie-choice', updateCookie);
      window.removeEventListener('nido:install-visible', showInstall);
      window.removeEventListener('nido:install-dismissed', hideInstall);
    };
  }, []);

  if (loading || user || dismissed || !cookieChosen || installVisible || HIDDEN_PATHS.some((path) => pathname.startsWith(path))) return null;

  const close = () => {
    try { sessionStorage.setItem(STORAGE_KEY, 'true'); } catch {}
    setDismissed(true);
  };

  return <Prompt role="complementary" aria-label="Ingresá o creá tu cuenta">
    <Close type="button" onClick={close} aria-label="Cerrar aviso"><FiX /></Close>
    <Icon><FiUserPlus /></Icon>
    <Copy><strong>¿Ya tenés cuenta en Nido?</strong><span>Ingresá o creá una para guardar favoritos y consultar propiedades.</span></Copy>
    <Actions>
      <LoginLink to="/login">Ingresar <FiArrowRight /></LoginLink>
      <RegisterLink to="/register">Crear cuenta</RegisterLink>
    </Actions>
  </Prompt>;
}

const Prompt = styled.aside`
  position:fixed;right:24px;bottom:24px;z-index:1500;display:grid;grid-template-columns:auto minmax(0,1fr);gap:12px;width:min(390px,calc(100vw - 32px));padding:18px 48px 17px 17px;border:1px solid rgba(198,111,78,.24);border-radius:18px;background:#fffdf9;box-shadow:0 20px 55px rgba(34,58,64,.18);color:#29474f;animation:guestWelcome .35s ease-out;
  @keyframes guestWelcome{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
  @media(max-width:650px){right:16px;bottom:16px;width:calc(100vw - 32px);padding:15px 42px 15px 14px;gap:10px}
`;
const Icon = styled.div`
  display:grid;width:36px;height:36px;place-items:center;border-radius:12px;background:#f7e5dc;color:#c66f4e;svg{width:18px;height:18px}
  @media(max-width:650px){width:34px;height:34px}
`;
const Copy = styled.div`
  min-width:0;strong,span{display:block}strong{font-size:.88rem;font-weight:850}span{margin-top:4px;color:#66797c;font-size:.76rem;line-height:1.4}
`;
const Actions = styled.div`
  grid-column:1/-1;display:flex;align-items:center;gap:9px;margin-top:2px;a{display:inline-flex;align-items:center;justify-content:center;gap:6px;min-height:35px;border-radius:999px;padding:0 13px;text-decoration:none;font-size:.74rem;font-weight:850;transition:.18s}svg{width:14px;height:14px}
`;
const LoginLink = styled(Link)`background:#c66f4e;color:#fff;&:hover{background:#af5e40;transform:translateY(-1px)}`;
const RegisterLink = styled(Link)`border:1px solid #dce4e3;color:#29474f;&:hover{border-color:#c66f4e;background:#fff7f2}`;
const Close = styled.button`
  position:absolute;top:10px;right:10px;display:grid;width:27px;height:27px;place-items:center;border:0;border-radius:50%;background:transparent;color:#6a7c7f;cursor:pointer;svg{width:16px;height:16px}&:hover{background:#f2f4f1;color:#29474f}
`;
